import { useMemo, useRef, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';

import { FormNames, SchoolReportDocument, SchoolReportSchema, YesNo } from '@youscience/commonapp-service-common';
import { brightpathTheme } from '@youscience/theme';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import useRecommenderStore from '@store/RecommenderStore';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { FormState } from '@constants/commonApp/types';

import { useUserProfile } from '@hooks/useUserProfile';

import { useRemoveShowErrorOnUnmount, removeShowErrorsFromUrl } from '@utils/navigation';
import GPA, { GPAFields } from './components/GPA';
import ClassRank, { ClassRankFields } from './components/ClassRank';
import Curriculum, { CurriculumFields } from './components/Curriculum';
import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import Ratings, { RatingsFields } from './components/Ratings';
import Transcripts, { TranscriptsFields } from './components/Transcripts';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import {
  checkCounselorProfileValidity,
  checkFormValidity,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
} from '../../common/formUtils';
import Submissions from '../../common/Submissions';
import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';
import { formatDateWithFallback } from '@utils/formatter';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';

const formName = FormNames.SECONDARY_REPORT;

export const SchoolReport = () => {
  useRemoveShowErrorOnUnmount();

  const { notify } = useNotificationStore((state) => state);
  const { userId } = useUserProfile();
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const schoolReport = useApplicantForm<SchoolReportDocument>(formName);
  const showRatings = recommenderProfile?.schoolProfile?.schoolProfile?.counselorAcademicRatings === YesNo.Yes;
  const initialFormState = useMemo(
    () => ({
      [TabSections.ClassRank]: schoolReport.content?.classRank,
      [TabSections.GPA]: schoolReport.content?.gpa,
      [TabSections.Curriculum]: schoolReport.content?.curriculum,
      [TabSections.Ratings]: showRatings ? schoolReport.content?.ratings : null,
      [TabSections.Transcripts]: schoolReport.content?.transcripts,
    }),
    [schoolReport.content, showRatings],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);
  const [expandedSection, setExpandedSection] = useState<TabSections | null>(TabSections.ClassRank);
  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const isSubmitted =
    schoolReport.metadata?.completionStatus === FormCompletionStatus.SUBMITTED ||
    schoolReport.metadata?.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = schoolReport.metadata?.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE;
  const { applicantId, recommenderId } = schoolReport.metadata;

  useEffect(() => {
    if (isShowErrors && schoolReport.content) {
      const errors = validateForm();
      setFormErrors(errors);

      const sections = [
        TabSections.ClassRank,
        TabSections.GPA,
        TabSections.Curriculum,
        TabSections.Ratings,
        TabSections.Transcripts,
      ] as const;

      const fullValidation = SchoolReportSchema.safeParse(form.current);

      if (!fullValidation.success) {
        const firstErrorSection = sections.find((section) => {
          return fullValidation.error.issues.some((issue) => issue.path[0] === section);
        });

        if (firstErrorSection) {
          setExpandedSection(firstErrorSection);
          return;
        }
      }

      const firstErrorSection = sections.find((section) => {
        const sectionData = schoolReport.content?.[section];

        if (!sectionData) {
          return false;
        }

        try {
          const sectionFormState = { [section]: sectionData };

          checkFormValidity(sectionFormState, SchoolReportSchema);

          return false;
        } catch (error) {
          return true;
        }
      });

      if (firstErrorSection) {
        setExpandedSection(firstErrorSection);
      }
    }
  }, [isShowErrors, schoolReport.content]);

  const toggleExpandedSelection = (section: TabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const getNextSection = (section: TabSections): TabSections | undefined => {
    switch (section) {
      case TabSections.ClassRank:
        return TabSections.GPA;
      case TabSections.GPA:
        return TabSections.Curriculum;
      case TabSections.Curriculum:
        return showRatings ? TabSections.Ratings : TabSections.Transcripts;
      case TabSections.Ratings:
        return TabSections.Transcripts;
    }
  };

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity(form.current, SchoolReportSchema);
    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (options?: {
    isHideNotification?: boolean;
    section?: keyof typeof initialFormState;
  }) => {
    const { isHideNotification, section } = options || {};
    const data = section ? { [section]: form.current[section] } : form.current;
    await schoolReport.save({ ...data, applicantId, recommenderId });

    if (!isHideNotification) {
      schoolReport.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }

    if (section && !schoolReport.isError) {
      toggleExpandedSelection(section);
      const nextSection = getNextSection(section);
      if (nextSection) toggleExpandedSelection(nextSection);
    }
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await schoolReport.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater({ isHideNotification: true });

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        removeShowErrorsFromUrl();
        await schoolReport.preview({ ...form.current, applicantId, recommenderId });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await schoolReport.submit({
      applicantId,
      recommenderId,
      counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
    });

    schoolReport.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await schoolReport.unsubmit({ applicantId, recommenderId });

    schoolReport.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!schoolReport.isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={brightpathTheme}>
      <FormHeader
        name="School report"
        tooltipTitle="The school report shares information about your student's academic history. It's also where you provide their most up-to-date transcripts."
        deadline={formatDateWithFallback(schoolReport.metadata?.deadline)}
        status={schoolReport.metadata?.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={schoolReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />
      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <>
          <ClassRank
            classRank={schoolReport.content?.classRank || recommenderProfile?.schoolProfile?.classRank}
            isExtended={expandedSection === TabSections.ClassRank}
            onExpand={() => toggleExpandedSelection(TabSections.ClassRank)}
            onFormChange={onFormChangeHandler<ClassRankFields>(TabSections.ClassRank)}
            onContinue={() => handleSaveForLater({ section: TabSections.ClassRank })}
            isSaved={schoolReport.sectionIsSaved.classRank}
          />

          <GPA
            gpa={schoolReport.content?.gpa || recommenderProfile?.schoolProfile?.gpa}
            isExtended={expandedSection === TabSections.GPA}
            onExpand={() => toggleExpandedSelection(TabSections.GPA)}
            onFormChange={onFormChangeHandler<GPAFields>(TabSections.GPA)}
            onContinue={() => {
              handleSaveForLater({ section: TabSections.GPA });
            }}
            isSaved={schoolReport.sectionIsSaved.gpa}
          />

          <Curriculum
            curriculum={schoolReport.content?.curriculum}
            isExtended={expandedSection === TabSections.Curriculum}
            onExpand={() => toggleExpandedSelection(TabSections.Curriculum)}
            onFormChange={onFormChangeHandler<CurriculumFields>(TabSections.Curriculum)}
            onContinue={() => handleSaveForLater({ section: TabSections.Curriculum })}
            isSaved={schoolReport.sectionIsSaved.curriculum}
          />

          {showRatings && (
            <Ratings
              ratings={schoolReport.content?.ratings}
              isExtended={expandedSection === TabSections.Ratings}
              onExpand={() => toggleExpandedSelection(TabSections.Ratings)}
              onFormChange={onFormChangeHandler<RatingsFields>(TabSections.Ratings)}
              onContinue={() => handleSaveForLater({ section: TabSections.Ratings })}
              isSaved={schoolReport.sectionIsSaved.ratings}
            />
          )}

          <Transcripts
            transcripts={
              schoolReport.content?.transcripts || (recommenderProfile?.schoolProfile?.transcripts as TranscriptsFields)
            }
            isExtended={expandedSection === TabSections.Transcripts}
            onExpand={() => toggleExpandedSelection(TabSections.Transcripts)}
            onFormChange={onFormChangeHandler<TranscriptsFields>(TabSections.Transcripts)}
            isSaved={schoolReport.sectionIsSaved.transcripts}
            applicantName={schoolReport.metadata?.studentName}
          />
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={schoolReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="School Report"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={schoolReport.isLoadingPreview}
          reviewType={reviewType}
          pdf={schoolReport.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </ThemeProvider>
  );
};
